import { useCallback, useMemo, useRef } from 'react';
import { FaRoute } from 'react-icons/fa6';
import { Checkbox } from '@ds';
import { FILTER_LIST, MARKER_TYPE_ICONS } from '@lp/features/RoutePlanner/config';
import { AvailabilityFilterType, MapFilterType } from '@lp/features/RoutePlanner/types';
import { UseFiltersType } from '@/hooks/useFilters';
import { AvailabilityPopover } from '../AvailabilityPopover';
import { convertToIanaTimezone } from '@/utils/helpers';

type MapFiltersProps = {
  showRoute: boolean;
  handleShowRoute: (value: boolean) => void;
} & Pick<UseFiltersType<Record<MapFilterType, boolean | string>>, 'filters' | 'updateFilters'> & { timezone: string };

export const MapFilters = ({ showRoute, handleShowRoute, filters, updateFilters, timezone }: MapFiltersProps) => {
  const orTimeValue = useRef(filters[MapFilterType.to] as string);
  const updateDateFrom = useCallback((value: string) => updateFilters(value, MapFilterType.from), [updateFilters]);
  const updateDateTo = useCallback((value: string) => updateFilters(value, MapFilterType.to), [updateFilters]);
  const convertedTimeZone = useMemo(() => convertToIanaTimezone(timezone), [timezone]);

  return (
    <div className="absolute z-402 flex gap-4 top-4 right-4 pl-1 flex-wrap justify-end">
      {!showRoute && (
        <>
          <div
            key="availability"
            className="flex shrink-0 px-1 pt-[7px] pb-[8px] bg-white rounded-3xl items-center shadow-xl text-h"
          >
            <span className="text-s-sb text-base-5 mr-3 ml-3">Available from</span>
            <AvailabilityPopover
              title="Available from"
              type={AvailabilityFilterType.Now}
              defaultValue={orTimeValue.current}
              value={filters.from as string}
              timezone={convertedTimeZone}
              onChange={updateDateFrom}
            />
            <span className="text-s-sb text-base-5 mr-3">till</span>
            <AvailabilityPopover
              title="Available to"
              type={AvailabilityFilterType.ORTime}
              defaultValue={orTimeValue.current}
              value={filters.to as string}
              timezone={convertedTimeZone}
              onChange={updateDateTo}
            />
          </div>
          {FILTER_LIST.map((f) => (
            <div key={f.type} className="flex shrink-0 px-1 bg-white rounded-3xl items-center shadow-xl text-h">
              <Checkbox
                size="sm"
                isSelected={filters[f.type] as boolean}
                isDisabled={f.isDisabled}
                onValueChange={(isSelected) => updateFilters(isSelected, f.type)}
              >
                {f.hasIcon ? MARKER_TYPE_ICONS[f.type] : <span className="text-s-sb text-base-5">{f.title}</span>}
              </Checkbox>
            </div>
          ))}
        </>
      )}
      <div className="flex shrink-0 px-1 bg-white rounded-3xl items-center shadow-xl text-h">
        <Checkbox size="sm" isSelected={showRoute} onValueChange={handleShowRoute}>
          <span className="text-s-sb text-base-5">
            <FaRoute size={16} />
          </span>
        </Checkbox>
      </div>
    </div>
  );
};
